import { Injectable } from "@angular/core";
import { BaseConfigService } from "./base-config.service";
import { StorageService } from "@core/storage/storage.service";
import { tileLayer, latLng } from "leaflet";

@Injectable({
  providedIn: "root",
})
export class ClientService extends BaseConfigService {

    private static singleton: ClientService;
    private _baseUrl = '{api}/{clientId}';
    private _baseUrlSimple = this._baseUrl; // '{api}';
    private _baseUrlWithVersion = this._baseUrl; // '{api}/{clientId}/v{version}';
    private _cdn = '{cdn}';
    private _clientCdnUrl = '{clientCdnUrl}/{clientId}';

    constructor(storageService: StorageService) {
        super(storageService);
        if (!ClientService.singleton) {
            ClientService.singleton = this;
        }
    }

    BrandsImage(imageId: string) {
        return this._resolveUrl(`https://nexgenavatars.blob.core.windows.net/{clientId}/brands/${imageId}.jpg`);
    }

    get distancePref() {
        return 'miles';
    }

    assessmentLibrary(id?: number) {
        if (!!id) {
            return this._resolveUrl(`${this._baseUrlWithVersion}/AssessmentFolders/${id}`);
        }
        return this._resolveUrl(`${this._baseUrlWithVersion}/AssessmentFolders`);
    }

    assessmentTypes() {
        return this._resolveUrl(`${this._baseUrlWithVersion}/Lookups/assessmenttypes`);
    }

    assessment(id: number) {
        return this._resolveUrl(`${this._baseUrlWithVersion}/assessments/${id}`);
    }

    assessmentClone(id: number) {
        return this._resolveUrl(`${this._baseUrlWithVersion}/assessments/${id}/clone`);
    }

    addAssessmentQuestion(id?: number) {
        if (!!id) {
            return this._resolveUrl(`${this._baseUrlWithVersion}/AssessmentQuestions/${id}`);

        }
        return this._resolveUrl(`${this._baseUrlWithVersion}/AssessmentQuestions`);
    }


    assessmentQuestionTypes() {
        return this._resolveUrl(`${this._baseUrlWithVersion}/Lookups/assessmentquestiontypes`);
    }

    completeAssessment(id: number) {
        return this._resolveUrl(`${this._baseUrlWithVersion}/assessments/${id}/complete`);
    }

    updateAssessmentDetails(id?: number) {
        if (!!id) {
            return this._resolveUrl(`${this._baseUrlWithVersion}/assessments/${id}`);
        }
        return this._resolveUrl(`${this._baseUrlWithVersion}/assessments`);
    }


    get BusinessAreas() {
        return this._resolveUrl(`${this._baseUrlWithVersion}/BusinessAreas`);
    }


    authoriseOffer(applicationId: number, code: string) {
        return this._resolveUrl(`${this._baseUrlWithVersion}/application/${applicationId}/AuthoriseOffer/${code}`);
    }

    get feedback() {
        return 'https://geniusatsfeeback.azurewebsites.net/api/GeniusAtsFeedback?code=zcRNj9enuH9MjziWyiYrfFmbDStnqJUbQRH9vtxl96glVvrO9DXpkQ==';
    }

    BusinessArea(id: number) {
        return this._resolveUrl(`${this._baseUrlWithVersion}/BusinessAreas/${id}`);
    }

    forwardCv() {
        return this._resolveUrl(`${this._baseUrlWithVersion}/Application/Bulk/ForwardCvsSendEmail`);
    }

    getForwardCv() {
        return this._resolveUrl(`${this._baseUrlWithVersion}/Application/Bulk/ForwardCvs`);
    }

    globalSearch(keywords: string) {
        return this._resolveUrl(`${this._baseUrlWithVersion}/search/global?keywords=${keywords}`);
    }

    overview() {
        return this._resolveUrl(`${this._baseUrlWithVersion}/dashboard/overview`);
    }

    globaldashboard() {
        return this._resolveUrl(`${this._baseUrlWithVersion}/dashboard/global`);
    }

    dashboardOutstandingApprovals() {
        return this._resolveUrl(`${this._baseUrlWithVersion}/dashboard/outstandingapprovals`);
    }

    addApplicant(id: number, emailAddress?: string) {
        if (!!emailAddress) {
            return this._resolveUrl(`${this._baseUrlWithVersion}/vacancy/${id}/AddApplication?emailAddress=${emailAddress}`);
        }
        return this._resolveUrl(`${this._baseUrlWithVersion}/vacancy/${id}/AddApplication`);
    }

    get reports() {
        return this._resolveUrl(`${this._baseUrlWithVersion}/reports`);
    }

    // Chart
    generateChart() {
        return this._resolveUrl(`${this._baseUrlWithVersion}/reports/graph`);
    }
    // reports
    savedReports() {
        return this._resolveUrl(`${this._baseUrlWithVersion}/SavedReport`);
    }
    // Reports with id
    savedReport(id: number) {
        return this._resolveUrl(`${this._baseUrlWithVersion}/SavedReport/${id}`);
    }


    document(id: string) {
        return this._resolveUrl(`${this._baseUrlWithVersion}/GetDocument/${id}`);
    }

    nexJobCv(id: string) {
        return this._resolveUrl(`${this._baseUrlWithVersion}/GetNexJobDocument/${id}/Download`);
    }

    get locations() {
        return this._resolveUrl(`${this._baseUrlWithVersion}/locations`);
    }

    locationsFiltered(showArchived: boolean) {
        return this._resolveUrl(`${this._baseUrlWithVersion}/locations?ShowArchive=${showArchived}`);
    }

    location(id: number) {
        return this._resolveUrl(`${this._baseUrlWithVersion}/locations/${id}`);
    }

    reverseLookup(lat: number, lng: number) {
        return `https://reverseproxyapi.basismedia.net/locationiq/v1/reverse.php?lat=${lat}&lon=${lng}&format=json`;
    }

    locationLookup(q: string) {
        return `https://eu1.locationiq.com/v1/search.php?key=${this.clientSettings.mapkey}&q=${q}&format=json`;
    }

    get userSettings() {
        return this._resolveUrl(`${this._baseUrlWithVersion}/account/settings`);
    }

    postcodeLookup(q: string) {
        return `https://eu1.locationiq.com/v1/search.php?key=${this.clientSettings.mapkey}&postalcode=${q}&format=json&&addressdetails=1`;
    }

    get cdnDefault() {
        return this._resolveUrl(`${this._cdn}`);
    }

    get cdnClientLogo() {
        return this._resolveUrl(`${this._clientCdnUrl}/logo.png`);
    }

    get cdnClientColors() {
        return this._resolveUrl(`${this._clientCdnUrl}/colors.json`);
    }

    getcdnImage(id: number | string, extension: string) {
        return this._resolveUrl(`${this._clientCdnUrl}/${id}.${extension}`);
    }

    get applicationSearch() {
        return this._resolveUrl(`${this._baseUrlSimple}/ApplicantSearch`);
    }

    get talentSearch() {
        return this._resolveUrl(`${this._baseUrlSimple}/TalentSearch`);
    }

    talentPools(id?: number) {
        if (!!id) {
            return this._resolveUrl(`${this._baseUrlSimple}/TalentPools/${id}`);
        }
        return this._resolveUrl(`${this._baseUrlSimple}/TalentPools`);
    }


    talentPoolsCandidates(id: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/TalentPools/${id}/candidates`);
    }

    findTalentForPool(id: number, emailAddress: string) {
        return this._resolveUrl(`${this._baseUrlSimple}/TalentPools/${id}/AddApplication?emailAddress=${emailAddress}`);
    }

    AddTalentToPool(id: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/TalentPools/${id}/AddApplication`);
    }

    RemoveTalentFromPool(id: number, userId: string) {
        return this._resolveUrl(`${this._baseUrlSimple}/TalentPools/${id}/DeleteCandidates/${userId}`);
    }

    get talentPoolLookups() {
        return this._resolveUrl(`${this._baseUrlSimple}/TalentPools/list`);
    }

    applicationDocuments(id: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/Application/${id}/Documents`);
    }

    applicationDocumentsWithPrivate(id: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/Application/${id}/Documents?includeNonCandidateVisible=true`);
    }

    get applicationDocument() {
        return this._resolveUrl(`${this._baseUrlSimple}/ApplicationDocuments`);
    }

    returnedApplicationDocuments(id: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/Application/${id}/ReturnedDocuments`);
    }

    returnedDashboardDocuments() {
        return this._resolveUrl(`${this._baseUrlSimple}/dashboard/returned-documents`);
    }

    applicantCv(id: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/Application/${id}/downloadCV`);
    }

    applicantCvModel(id: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/Application/${id}/DownloadCVModel`);
    }

    onboardingPackForVacancy(id: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/vacancy/${id}/OnboardingPack`);
    }

    isOnboardingPackAssigned(id: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/OnboardingPacks/IsAssignedTo/${id}`);
    }

    onboardingPackAssign(id: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/OnboardingPacks/AssignTo/${id}`);
    }

    removeApplicationDocuments(id: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/ApplicationDocuments/${id}`);
    }


    forgotPassword(id: string) {
        const email = encodeURIComponent(id);
        return this._resolveUrl(`${this._baseUrl}/ForgotPassword?emailAddress=${email}`);
    }

    get resetPassword() {
        return this._resolveUrl(`${this._baseUrl}/resetpassword`);
    }

    get rootResetPassword() {
        return this._resolveUrl(`${this._baseUrl}/account/resetpassword`);
    }

    get logOff() {
        return this._resolveUrl(`${this._baseUrl}/account/logoff`);
    }

    get systemUserPools() {
        return this._resolveUrl(`${this._baseUrlSimple}/pools`);
    }

    userPools(id: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/pools/${id}`);
    }

    scoringSheets() {
        return this._resolveUrl(`${this._baseUrlSimple}/vacancy/scoring`);
    }

    scoringSheet(id: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/vacancy/scoring/${id}`);
    }

    scoringSheetQuestionAdd(scoringId: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/vacancy/scoring/${scoringId}/question`);
    }

    scoringSheetQuestionEdit(scoringId: number, scoringQuestionId: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/vacancy/scoring/${scoringId}/question/${scoringQuestionId}`);
    }

    get onboardingPacks() {
        return this._resolveUrl(`${this._baseUrlSimple}/OnboardingPacks`);
    }

    onboardingUpload(packId: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/OnboardingPacks/${packId}/upload`);
    }

    generalDocuments() {
        return this._resolveUrl(`${this._baseUrlSimple}/GeneralDocuments`);
    }

    // generalDocumentsId() {
    //     return this._resolveUrl(`${this._baseUrlSimple}/GeneralDocuments/${id}`)
    // }

    get mailMergeOptions() {
        return this._resolveUrl(`${this._baseUrlSimple}/MailMerges/Options`);
    }

    get onboardingDocuments() {
        return this._resolveUrl(`${this._baseUrlSimple}/OnboardingPacks/AllDocuments`);
    }

    getOnboardingPackDocument(documentId: number, onboardingPackId: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/OnboardingPacks/${onboardingPackId}/Document/${documentId}`);
    }

    onboardingPackDocument(id: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/OnboardingPacks/${id}/Document`);
    }

    onboardingPack(id: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/OnboardingPacks/${id}`);
    }

    get privillages() {
        return this._resolveUrl(`${this._baseUrlSimple}/Pool/Privileges`);
    }

    get allBrands() {
        return this._resolveUrl(`${this._baseUrlSimple}/brands`);
    }

    brand(id: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/brands/${id}`);
    }

    brandCreate() {
        return this._resolveUrl(`${this._baseUrlSimple}/brands/new`);
    }

    brandUpdate(id: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/brands/${id}/update`);
    }

    get changePassword() {
        return this._resolveUrl(`${this._baseUrlSimple}/account/changePassword`);
    }

    brandImage(brandId: string) {
        return this._resolveUrl(`${this._baseUrlSimple}/brands/${brandId}/ChangeImage`);
    }

    poolPrivillages(id: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/Pools/${id}`);
    }

    profileTemplateImageUrl(imageId: string) {
        return this._resolveUrl(`https://nexgenavatars.blob.core.windows.net/{clientId}/brands/${imageId}.jpg`);
    }
    
    profileTemplateFiltered(showArchived: boolean) {
        return this._resolveUrl(`${this._baseUrl}/profileTemplates?showArchived=${showArchived}`);
    }

    profileTemplate() {
        return this._resolveUrl(`${this._baseUrl}/profileTemplates`);
    }

    profileTemplateId(id: number) {
        return this._resolveUrl(`${this._baseUrl}/profileTemplates/${id}`);
    }
    
    profileTemplateChangeImage(id: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/profileTemplates/${id}/ChangeImage`);
    }

    updatePoolPrivleges(id: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/pool/${id}/privileges`);
    }

    updatePoolPrivForUser(poolId: number, userId: string) {
        return this._resolveUrl(`${this._baseUrlSimple}/pool/${poolId}/user?userId=${userId}`);
    }

    systemUsers(userId?: string) {
        if (!!userId) {
            return this._resolveUrl(`${this._baseUrlSimple}/ControlPanelUsers/${userId}`);
        }
        return this._resolveUrl(`${this._baseUrlSimple}/ControlPanelUsers`);
    }

    deleteUserRole(userId: string, role: string) {
        return this._resolveUrl(`${this._baseUrlSimple}/ControlPanelUsers/${userId}/Remove/${role}`);
    }

    getAboutMe() {
        return this._resolveUrl(`${this._baseUrlSimple}/account/about`);
    }

    lockUser(userId: string) {
        return this._resolveUrl(`${this._baseUrlSimple}/ControlPanelUsers/${userId}/Lock`);
    }

    unlockUser(userId: string) {
        return this._resolveUrl(`${this._baseUrlSimple}/ControlPanelUsers/${userId}/Unlock`);
    }

    applicationOfferAuthorisers(applicationId: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/application/${applicationId}/OfferAuthorisers`);
    }

    getApplication(applicationId: number) {
        return this._resolveUrl(`${this._baseUrlWithVersion}/application/${applicationId}`);
    }

    getApplicationExtended(applicationId: number) {
        return this._resolveUrl(`${this._baseUrlWithVersion}/application/${applicationId}/expand`);
    }

    getNexjobInfo(id: string, type: string) {
        return this._resolveUrl(`${this._baseUrlWithVersion}/nexjob/${id}/${type}`);

    }

    getApplicationAssessments(applicationId: number) {
        return this._resolveUrl(`${this._baseUrlWithVersion}/application/${applicationId}/assessments`);
    }

    putPersonalDetails(applicationId: number) {
        return this._resolveUrl(`${this._baseUrlWithVersion}/Applications/${applicationId}/PersonalDetails`);
    }

    postAnonymisationRequest(userId: string) {
        return this._resolveUrl(`${this._baseUrlWithVersion}/candidate/${userId}/AnonymisationRequest`);
    }

    getPreviewCV(applicationId: number, documentId?: string) {
        if (!!documentId) {
            return this._resolveUrl(`${this._baseUrlWithVersion}/Application/${applicationId}/PreviewCV/${documentId}`);
        }
        return this._resolveUrl(`${this._baseUrlWithVersion}/Application/${applicationId}/PreviewCV`);
    }

    getCandidateAppointments(applicationId: number) {
        return this._resolveUrl(`${this._baseUrlWithVersion}/Application/${applicationId}/GetAppointments`);
    }

    getApplicationAudit(applicationId: number) {
        return this._resolveUrl(`${this._baseUrlWithVersion}/Application/${applicationId}/Audit`);
    }

    getApplicationAuditEmail(applicationId: number, emailHistoryId: number) {
        return this._resolveUrl(`${this._baseUrlWithVersion}/Application/${applicationId}/Email/${emailHistoryId}`);
    }

    getApplicationEmployers(applicationId: number) {
        return this._resolveUrl(`${this._baseUrlWithVersion}/Application/${applicationId}/Employers`);
    }

    getApplicationEducation(applicationId: number) {
        return this._resolveUrl(`${this._baseUrlWithVersion}/Application/${applicationId}/Education`);
    }

    getApplicationEmployer(applicationId: number) {
        return this._resolveUrl(`${this._baseUrlWithVersion}/Application/${applicationId}/Employer`);
    }


    getApplicationNote(applicationId: number, applicationHistoryId?: number) {
        if (!!applicationHistoryId) {
            return this._resolveUrl(`${this._baseUrlWithVersion}/Application/${applicationId}/Note/${applicationHistoryId}`);
        }
        return this._resolveUrl(`${this._baseUrlWithVersion}/Application/${applicationId}/Note`);

    }

    getOtherApplications(applicationId: number) {
        return this._resolveUrl(`${this._baseUrlWithVersion}/Application/${applicationId}/OtherApplications`);
    }

    getApplicationReferences(applicationId: number) {
        return this._resolveUrl(`${this._baseUrlWithVersion}/Application/${applicationId}/References`);
    }

    getApplicationReference(applicationId: number) {
        return this._resolveUrl(`${this._baseUrlWithVersion}/Application/${applicationId}/Reference`);
    }

    getApplicationsMilestones(applicationId: number) {
        return this._resolveUrl(`${this._baseUrlWithVersion}/Application/${applicationId}/Milestones`);
    }

    getMilestoneLookup() {
        return this._resolveUrl(`${this._baseUrlWithVersion}/Lookups/ApplicationMilestones`);
    }

    getScoringForApplication(applicationId: number) {
        return this._resolveUrl(`${this._baseUrlWithVersion}/Application/${applicationId}/scoring`);
    }

    getAllInterviewsForVacancy(vacancyId: number) {
        return this._resolveUrl(`${this._baseUrlWithVersion}/VacancyInterviews/${vacancyId}/All`);
    }

    getInterviewForApplicant(applicationId: number, interviewId: number) {
        return this._resolveUrl(`${this._baseUrlWithVersion}/ApplicationInterviews/${applicationId}/${interviewId}`);
    }

    SubmitInterviewForApplicant(applicationId: number, interviewId: number) {
        return this._resolveUrl(`${this._baseUrlWithVersion}/ApplicationInterviews/${applicationId}/Submit/${interviewId}`);
    }

    changeEmailForCandidate(applicationId: number) {
        return this._resolveUrl(`${this._baseUrlWithVersion}/Applications/${applicationId}/EmailAddress`);
    }

    reassignApplication(applicationId: number) {
        return this._resolveUrl(`${this._baseUrlWithVersion}/Applications/${applicationId}/Reassign`);
    }

    getVacancyAppointments(vacancyId: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/vacancy/${vacancyId}/getAppointments`);
    }

    archiveVacancy(vacancyId: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/vacancy/${vacancyId}/archive`);
    }

    bulkArchiveVacancy() {
        return this._resolveUrl(`${this._baseUrlSimple}/vacancy/bulk/archive`);
    }

    unarchiveVacancy(vacancyId: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/vacancy/${vacancyId}/unarchive`);
    }

    openVacancy(vacancyId: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/vacancy/${vacancyId}/open`);
    }

    closeVacancy(vacancyId: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/vacancy/${vacancyId}/close`);
    }

    getCosts(vacancyId: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/vacancy/${vacancyId}/costs`);
    }

    getAgentDuplicates(vacancyId: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/agent-suggestions/${vacancyId}`);
    }

    archiveAgentDuplicate(vacancyId: number, profileId: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/agent-suggestions/${vacancyId}/archive/${profileId}`);
    }

    compareAgentDuplicate(duplicateId: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/agent-suggestions/${duplicateId}/compare`);
    }

    addCost() {
        return this._resolveUrl(`${this._baseUrlSimple}/vacancyCosts`);
    }

    cost(id: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/vacancyCosts/${id}`);
    }

    costLookup() {
        return this._resolveUrl(`${this._baseUrlSimple}/Lookups/vacancycosttypes`);
    }

    getAppointments(id?: number) {
        if (!!id) {
            return this._resolveUrl(`${this._baseUrlSimple}/appointments/${id}`);
        }
        return this._resolveUrl(`${this._baseUrlSimple}/appointments`);
    }

    getMSConnection()
    {
        return this._resolveUrl(`${this._baseUrlSimple}/Appointments/MSConnection`);
    }

    getMsGraphDeviceLogin()
    {
        return this._resolveUrl(`${this._baseUrlSimple}/Appointments/MyMicrosoftGraph1`);
    }

    getMsGraphDeviceCode()
    {
        return this._resolveUrl(`${this._baseUrlSimple}/Appointments/MyMicrosoftGraph2`);
    }

    getMsGraphCalendars()
    {
        return this._resolveUrl(`${this._baseUrlSimple}/Appointments/MyMicrosoftAppointments`);
    }

    getMsGraphCreateEvent(id: number)
    {
        return this._resolveUrl(`${this._baseUrlSimple}/Appointments/MyMicrosoftCreateEvent/${id}`);
    }

    timeslots() {
        return this._resolveUrl(`${this._baseUrlSimple}/timeslots`);
    }

    deleteAppointmentUser(appointmentUserid: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/appointments/DeleteUser?id=${appointmentUserid}`);
    }

    cancelAppointment(id: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/appointments/cancel?id=${id}`);
    }

    dashboardVacancies(id?: number, mine?: boolean) {
        if (!!mine) {
            return this._resolveUrl(`${this._baseUrlSimple}/dashboard/vacancy?mine=true`);
        }

        if (!!id) {
            return this._resolveUrl(`${this._baseUrlSimple}/dashboard/vacancy/${id}`);
        }
        return this._resolveUrl(`${this._baseUrlSimple}/dashboard/vacancy`);
    }

    vacancyQuestions(id: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/vacancy/${id}/vacancyQuestions`);
    }

    vacancyQuestionOrder(id: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/vacancy/${id}/vacancyQuestions/Order`);
    }

    removeVacancyQuestions(vacancyId: number, vacanctQuestionId: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/vacancy/${vacancyId}/vacancyQuestions/${vacanctQuestionId}`);
    }

    vacancyAsessments(id: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/vacancy/${id}/vacancyAssessments`);
    }


    vacancyAsessment(id: number, assessmentId: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/vacancy/${id}/vacancyAssessments/${assessmentId}`);
    }

    AssessmentFolders() {
        return this._resolveUrl(`${this._baseUrlSimple}/AssessmentFolders`);
    }

    dashboardVacancyApplicationsV2(id: number) {
        return this._resolveUrl(`${this._baseUrl}/dashboard/vacancy/${id}/applications`);
    }

    vacancyAudit(id: number) {
        return this._resolveUrl(`${this._baseUrlWithVersion}/vacancy/${id}/Audit`);
    }

    vacancyProcess(id: number) {
        return this._resolveUrl(`${this._baseUrlWithVersion}/vacancy/${id}/Workflow`);
    }

    vacanyQualities(id: number) {
        return this._resolveUrl(`${this._baseUrl}/vacancy/${id}/qualities`);
    }
    vacanyQuality(id: number) {
        return this._resolveUrl(`${this._baseUrl}/vacancy/${id}/quality`);
    }

    vacanyQualityUpdate(vacancyId: number, qualityId: number) {
        return this._resolveUrl(`${this._baseUrl}/vacancy/${vacancyId}/quality/${qualityId}`);
    }

    qualities(id?: number) {
        if (!!id) {
            return this._resolveUrl(`${this._baseUrl}/vacancy/qualities/${id}`);
        }
        return this._resolveUrl(`${this._baseUrl}/vacancy/qualities`);
    }

    qualitiesLookup(id?: number) {
        if (!!id) {
            return this._resolveUrl(`${this._baseUrl}/system/lookups/qualities/${id}`);
        }
        return this._resolveUrl(`${this._baseUrl}/system/lookups/qualities`);
    }


    AICompany(id?: number) {
        if (!!id) {
            return this._resolveUrl(`${this._baseUrlSimple}/ai/company/${id}`);
        }
        return this._resolveUrl(`${this._baseUrlSimple}/ai/company`);
    }

    AIVacancyCompany(id: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/ai/company/vacancy/${id}`);
    }

    AIJobTitle(id?: number) {
        if (!!id) {
            return this._resolveUrl(`${this._baseUrlSimple}/ai/jobtitle/${id}`);
        }
        return this._resolveUrl(`${this._baseUrlSimple}/ai/jobtitle`);
    }


    AIVacancyJobTitle(id: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/ai/jobtitle/vacancy/${id}`);
    }

    vacancyTemplates() {
        return this._resolveUrl(`${this._baseUrl}/vacancies/templates`);
    }

    applicationProgress(vacancyId: number, applicationStatusId: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/vacancy/${vacancyId}/application/Bulk/ProgressToStage/${applicationStatusId}`);
    }

    applicationOffer(applicationId: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/application/${applicationId}/offer`);
    }

    applicationOfferDeauthorise(applicationId: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/application/${applicationId}/offer/Deauthorise`);
    }

    appointmentTimeslots(appointmentId: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/timeslots/${appointmentId}`);
    }

    appointmentTimeslotsRemoveUser(appointmentId: number, userId: string) {
        return this._resolveUrl(`${this._baseUrlSimple}/timeslots/${appointmentId}/invitees/${userId}`);
    }

    bookTimeslot(timeslotId: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/timeslots/${timeslotId}/book`);
    }

    applicationProgressWithEmailCalendar(vacancyId: number, applicationStatusId: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/vacancy/${vacancyId}/Application/Bulk/ProgressToStage/${applicationStatusId}/EmailCalendar`);
        //    return this._resolveUrl(`${this._baseUrlSimple}/Application/Bulk/ProgressToStage/${applicationStatusId}/EmailCalendar?vacancyId=${vacancyId}`);
    }

    applicationProgressWitPreviousAppointment(appointmentId: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/Appointments/${appointmentId}/AddApplications`);
    }

    appointmentTimeslot(appointmentid: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/timeslots/${appointmentid}`);
    }

    shortListApplicants() {
        return this._resolveUrl(`${this._baseUrlSimple}/application/bulkStar`);
    }

    flagApplicants() {
        return this._resolveUrl(`${this._baseUrlSimple}/application/bulkStar2`);
    }

    toggleShortlist(applicationId: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/application/${applicationId}/ToggleStar`);
    }

    toggleShortlist2(applicationId: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/application/${applicationId}/ToggleStar2`);
    }

    applicationRead() {
        return this._resolveUrl(`${this._baseUrlSimple}/application/BulkRead`);
    }

    vacancySummary(id: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/dashboard/vacancy/${id}/summary`);
    }

    applicationUread(applicationId: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/application/${applicationId}/Read?read=false`);
    }

    emails(id?: number | string) {
        if (!!id) {
            return this._resolveUrl(`${this._baseUrlSimple}/emails/${id}`);
        }
        return this._resolveUrl(`${this._baseUrlSimple}/emails`);
    }

    agencies() {
        return this._resolveUrl(`${this._baseUrlSimple}/agents`);
    }

    sendBulkEmail() {
        return this._resolveUrl(`${this._baseUrlSimple}/Application/Bulk/Email`);
    }

    sendBulkSms() {
        return this._resolveUrl(`${this._baseUrlSimple}/sms/sendBulk`);
    }

    talentPoolsLookup() {
        return this._resolveUrl(`${this._baseUrlSimple}/TalentPools/List`);
    }

    bulkAddToTalentPool(talentPoolId: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/Application/Bulk/TalentPool/${talentPoolId}`);
    }

    bulkAddToVacancy(vacancyId: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/Vacancy/${vacancyId}/CloneApplicationBulk`);
    }

    applicationScoresCompare() {
        return this._resolveUrl(`${this._baseUrlSimple}/Application/scoring/compare`);
    }

    workflowCells(id?: number | string) {
        if (!!id) {
            return this._resolveUrl(`${this._baseUrlSimple}/ApplicationStatus/${id}`);
        }
        return this._resolveUrl(`${this._baseUrlSimple}/ApplicationStatus`);
    }

    workflowEmailRecipients() {
        return this._resolveUrl(`${this._baseUrlSimple}/Lookups/Emailrecipienttypes`);
    }

    interviewers() {
        return this._resolveUrl(`${this._baseUrlSimple}/Lookups/interviewers?cache=infinite`);
    }

    interviews() {
        return this._resolveUrl(`${this._baseUrlSimple}/Interviews/all?includeArchived=true`);
    }

    interviewsForVacancy(interviewId: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/Interviews/${interviewId}`);
    }

    createInterview() {
        return this._resolveUrl(`${this._baseUrlSimple}/Interviews/Create`);
    }

    updateInterview(interviewId: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/Interviews/${interviewId}/Update`);
    }

    deleteInterview(interviewId: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/Interviews/${interviewId}/Archive`);
    }

    cloneInterview(interviewId: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/Interviews/${interviewId}/Clone`);
    }

    assignWorkflowToVacancy(vacancyId: number, workflowId: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/Vacancies/${vacancyId}/CloneStages/${workflowId}`);
    }

    jobBoards() {
        return this._resolveUrl(`${this._baseUrlSimple}/jobBoards`);
    }

    jobBoardStates(id: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/vacancy/${id}/jobBoardActiveStates`);
    }

    getVacancyProcess(id?: number | string) {
        if (!!id) {
            return this._resolveUrl(`${this._baseUrlSimple}/VacancyProcesses/${id}`);
        }
        return this._resolveUrl(`${this._baseUrlSimple}/VacancyProcesses?cache=infinite`);
    }

    vacancyProgessCreate() {
        return this._resolveUrl(`${this._baseUrlWithVersion}/VacancyProcesses`);
    }


    vacancyLookupBusinessAreas() {
        return this._resolveUrl(`${this._baseUrlSimple}/Lookups/BusinessAreas?cache=infinite`);
    }

    vacancyAuth(id: string, linkkey: string) {
        return this._resolveUrl(`${this._baseUrlSimple}/vacancy/${id}/authorise/${linkkey}`);
    }

    vacancyStatuses() {
        return this._resolveUrl(`${this._baseUrlSimple}/Lookups/VacancyStatuses?cache=infinite`);
    }

    get vacancySalaryGrades() {
        return this._resolveUrl(`${this._baseUrlSimple}/VacancySalaryGrades`);
    }

    vacancySalaryGrade(id: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/VacancySalaryGrades/${id}`);
    }

    get vacancyDepartments() {
        return this._resolveUrl(`${this._baseUrlSimple}/locationRegions`);
    }

    get allSystemEmails() {
        return this._resolveUrl(`${this._baseUrlSimple}/emails`);
    }

    get mailMergeFields() {
        return this._resolveUrl(`${this._baseUrlSimple}/emails/MailMergeFields`);

    }

    getEmail(id: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/emails/${id}`);
    }

    getAllSms() {
        return this._resolveUrl(`${this._baseUrlSimple}/sms-templates`);
    }

    getSms(id: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/sms-templates/${id}`);
    }

    attachmentDocuments(id?: number) {
        if (!!id) {
            return this._resolveUrl(`${this._baseUrlSimple}/AttachmentDocuments/${id}`);
        }
        return this._resolveUrl(`${this._baseUrlSimple}/AttachmentDocuments`);
    }

    vacancyDepartment(id: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/locationRegions/${id}`);
    }

    get vacancySectors() {
        return this._resolveUrl(`${this._baseUrlSimple}/locationAreas`);
    }

    get agents() {
        return this._resolveUrl(`${this._baseUrlSimple}/agents`);
    }

    agent(id: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/agents/${id}`);
    }

    agentUsers(id: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/agent/${id}/agentUsers`);
    }

    agentUserDelete(agentId: number, agentUserId: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/agent/${agentId}/${agentUserId}`);
    }

    archiveAgentUser(id: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/agents/${id}/archive`);
    }

    get agentUser() {
        return this._resolveUrl(`${this._baseUrlSimple}/agentUsers`);
    }

    updatAgentUser(id: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/agentUsers/${id}`);
    }

    vacancySector(id: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/locationAreas/${id}`);
    }

    vacancySearch() {
        return this._resolveUrl(`${this._baseUrlSimple}/vacancySearch`);
    }

    vacancyLookupLocations(keyword: string) {
        return this._resolveUrl(`${this._baseUrlSimple}/Lookups/LocationSearch?keyword=${keyword}`);
    }

    LookupLists(list: string[]) {
        return this._resolveUrl(`${this._baseUrl}/LookupLists?lookups=${list.join('&lookups=')}&cache=infinite`);
    }

    vacancyDescriptions(id: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/Vacancy/${id}/VacancyDescriptions`);
    }

    reorderVacancyDescription(id: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/Vacancy/${id}/VacancyDescriptionsOrder`);
    }

    createVacancyDescription() {
        return this._resolveUrl(`${this._baseUrlSimple}/VacancyDescriptions`);
    }

    deleteVacancyDescription(id: number) {
        return this._resolveUrl(`${this._baseUrlSimple}//VacancyDescriptions/${id}`);
    }

    deleteVacancy(vacancyId: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/vacancy/${vacancyId}`);
    }

    resetVacancyAuthorisation(vacancyId: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/vacancy/${vacancyId}/reset-authorisation`);
    }

    launchVacancy(vacancyId: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/vacancy/${vacancyId}/launch`);
    }

    canIEdit(vacancyId: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/vacancy/${vacancyId}/canIEdit`);

    }


    vacancyAgencyQuota(id?: number, vacancyId?: number) {
        if (!!id && !vacancyId) {
            return this._resolveUrl(`${this._baseUrlSimple}/VacancyAgentQuotas/${id}`);
        }
        if (!!vacancyId) {
            return this._resolveUrl(`${this._baseUrlSimple}/vacancy/${vacancyId}/agentQuotas`);
        }
        return this._resolveUrl(`${this._baseUrlSimple}/VacancyAgentQuotas`);
    }

    vacancies(id?: number) {
        if (!!id) {
            return this._resolveUrl(`${this._baseUrlSimple}/vacancies/${id}`);
        }
        return this._resolveUrl(`${this._baseUrlSimple}/vacancies`);
    }

    similarVacancies(id: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/vacancy/${id}/similar`);
    }

    vacancyClone(id?: number, isTemplate?: boolean) {
        return this._resolveUrl(`${this._baseUrlSimple}/Vacancy/${id}/Clone?asTemplate=${isTemplate}`);
    }

    vacanciesByStatus(id: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/vacancies/status/${id}`);
    }

    vacancyDocuments(id: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/vacancy/${id}/documents`);
    }

    vacancyDocument(id: number, documentId: string) {
        return this._resolveUrl(`${this._baseUrlSimple}/vacancy/${id}/document/${documentId}`);
    }

    addVacancyDocument(id: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/vacancy/${id}/document`);
    }

    get vacancyTemplate() {
        return this._resolveUrl(`${this._baseUrlSimple}/vacancies/templates`);
    }

    vacancyInterviewers(vacancyId: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/Vacancy/${vacancyId}/Interviewers`);
    }

    controlPanelUser() {
        return this._resolveUrl(`${this._baseUrlSimple}/ControlPanelUsers`);
    }

    controlPanelUserSimpleCreate() {
        return this._resolveUrl(`${this._baseUrlSimple}/ControlPanelUsers/Simple/Create`);
    }

    controlPanelUserLookup() {
        return this._resolveUrl(`${this._baseUrlSimple}/lookups/ControlPanelUsers?cache=infinite`);
    }

    vacancyInterviews(vacancyId: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/VacancyInterviews/${vacancyId}/All`);
    }

    archiveVacancyInterview(interviewId: number, vacancyId: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/VacancyInterviews/${vacancyId}/${interviewId}/archive`);
    }

    vacancyApplicationSections(vacancyId: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/vacancy/${vacancyId}/applicationSections`);
    }

    cloneVacancy(vacancyId: number, asTemplate: boolean) {
        return this._resolveUrl(`${this._baseUrlSimple}/vacancy/${vacancyId}/clone?asTemplate=${asTemplate}`);
    }

    assignInterviewToVacancy(interviewId: number, vacancyId: number) {
        return this._resolveUrl(`${this._baseUrlWithVersion}/VacancyInterviews/${vacancyId}/Assign/${interviewId}`);
    }

    getVacancyWorkflow(vacancyId: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/Vacancies/${vacancyId}/Stages`);
    }

    vacancyDescriptionForVacancy(vacancyId: number) {
        return this._resolveUrl(`${this._baseUrlSimple}Vacancy/${vacancyId}/VacancyDescriptions`);
    }



    vacancyAwaitingAuthorisation() {
        return this._resolveUrl(`${this._baseUrl}/Vacancy/AwaitingAuthorisation`);
    }

    resendVacancyAuthEmails(id: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/Vacancy/${id}/ResendAuthorisationEmails`);
    }

    getVacancyAuthorisers(Id: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/Vacancy/${Id}/Authorisers`);
    }

    get authorisers() {
        return this._resolveUrl(`${this._baseUrl}/Lookups/Authorisers`);
    }

    deleteCPUser(id: string) {
        return this._resolveUrl(`${this._baseUrl}/ControlPanelUsers/${id}/RemoveAllPrivileges`);
    }

    getMyAppointments() {
        return this._resolveUrl(`${this._baseUrl}/Appointments/MyAppointments`);
    }

    userCheckLists() {
        return this._resolveUrl(`${this._baseUrl}/userchecklists`);
    }

    userCheckListsEdit(Id: number) {
        return this._resolveUrl(`${this._baseUrl}/userchecklists/${Id}`);
    }

    userCheckListsToggleComplete(Id: number) {
        return this._resolveUrl(`${this._baseUrl}/userchecklists/${Id}/togglecomplete`);
    }

    userCheckListsArchive(Id: number) {
        return this._resolveUrl(`${this._baseUrl}/userchecklists/${Id}/archive`);
    }

    videoInterview(appointmentId: number, appointmentUserId: string) {
        return this._resolveUrl(`${this._baseUrl}/Appointments/${appointmentId}/Video/${appointmentUserId}`);
    }

    vacancySocial(Id: number) {
        return this._resolveUrl(`${this._baseUrl}/vacancies/${Id}/social`);
    }

    public mapOptions = {
        layers: [
            tileLayer(`https://{s}-tiles.locationiq.com/v2/obk/r/{z}/{x}/{y}.png?key=${this.clientSettings.mapkey}`, { maxZoom: 18, attribution: '...' })
        ],
        zoom: 6,
        zoomControl: true,
        center: latLng(54.0, -2.0)
    };

    public mapOptionsZoom = {
        layers: [
            tileLayer(`https://{s}-tiles.locationiq.com/v2/obk/r/{z}/{x}/{y}.png?key=${this.clientSettings.mapkey}`, { maxZoom: 18, attribution: '...' })
        ],
        zoom: 5,
        zoomControl: false,
        center: latLng(55.0, -3.0)
    };

    vacancyLists() {
        return this._resolveUrl(`${this._baseUrl}/Vacancy/VacancyLists`);
    }

    vacancyListSaved(vacancyId: number) {
        return this._resolveUrl(`${this._baseUrl}/Vacancy/${vacancyId}/VacancyLists/VacancySavedOptions`);
    }

    vacancyListUpdate(vacancyId: number) {
        return this._resolveUrl(`${this._baseUrl}/Vacancy/${vacancyId}/VacancyLists/UpdateVacancy`);
    }

    vacancySimilarApplicants(vacancyId: number, forename: string, surname: string) {
        return this._resolveUrl(`${this._baseUrlWithVersion}/Applications?filters.forename=${forename}&filters.surname=${surname}&filters.vacancyId=${vacancyId}`);
    }

    vacancyGenerate() {
        return this._resolveUrl(`${this._baseUrl}/GenerateOpenAIVacancyDescription`);
    }

    titleOverrides(id: number) {
      return this._resolveUrl(
        `${this._baseUrl}/VacancyDescriptionsTitleOverride/${id}`
      );
    }

    addTitleOverride() {
      return this._resolveUrl(
        `${this._baseUrl}/VacancyDescriptionsTitleOverride`
      );
    }
    // Report search

    // NHS Jobs - temp / todo
    nhsJobsLocations() {
        return this._resolveUrl(`${this._baseUrlWithVersion}/nhs-jobs/locations`);
    }

    nhsJobsLookups() {
        return this._resolveUrl(`${this._baseUrlWithVersion}/nhs-jobs/lookups`);
    }

    nhsJobsSaveVacancy(vacancyId: number) {
        return this._resolveUrl(`${this._baseUrlWithVersion}/nhs-jobs-tab/vacancy/${vacancyId}`);
    }

    nhsJobsSyncVacancy(vacancyId: number) {
        return this._resolveUrl(`${this._baseUrlWithVersion}/nhs-jobs/vacancy/${vacancyId}`);
    }

    nhsJobsSupportingDocuments(vacancyId: number) {
        return this._resolveUrl(`${this._baseUrlWithVersion}/nhs-jobs-tab/vacancy/${vacancyId}/supporting-documents`);
    }

    nhsJobsAddSupportingDocument(vacancyId: number, documentId: number) {
        return this._resolveUrl(`${this._baseUrlWithVersion}/nhs-jobs-tab/vacancy/${vacancyId}/supporting-documents/${documentId}`);
    }

    nhsJobsDeleteSupportingDocument(vacancyId: number, supportingDocumentId: number) {
        return this._resolveUrl(`${this._baseUrlWithVersion}/nhs-jobs-tab/vacancy/${vacancyId}/supporting-documents/${supportingDocumentId}`);
    }

    nhsJobsPublishSupportingDocument(vacancyId: number, supportingDocumentId: number) {
        return this._resolveUrl(`${this._baseUrlWithVersion}/nhs-jobs/vacancy/${vacancyId}/supporting-documents/${supportingDocumentId}`);
    }

    nhsJobsPostAudits(vacancyId: number) {
        return this._resolveUrl(`${this._baseUrlWithVersion}/nhs-jobs-tab/vacancy/${vacancyId}/audit`);
    }

    nhsJobsApplicationsCriteria(applicationId: number) {
        return this._resolveUrl(`${this._baseUrlWithVersion}/Application/${applicationId}/Criteria`);
    }
    
    // RTW / Right To Work
    rtwResponses(applicationId: number) {
        return this._resolveUrl(`${this._baseUrlWithVersion}/Application/${applicationId}/rtw/responses`);
    }

    rtwCandidateResponses(applicationId: number) {
        return this._resolveUrl(`${this._baseUrlWithVersion}/Application/${applicationId}/rtw/responses/all`);
    }

    rtwDownload(applicationId: number, rtwId: number) {
        return this._resolveUrl(`${this._baseUrlWithVersion}/Application/${applicationId}/rtw/${rtwId}/download`);
    }

    rtwAuditCreate(applicationId: number, rtwId: number) {
        return this._resolveUrl(`${this._baseUrlWithVersion}/Application/${applicationId}/rtw/${rtwId}/add-audit`);
    }

    rtwAudits(applicationId: number) {
        return this._resolveUrl(`${this._baseUrlWithVersion}/Application/${applicationId}/rtw/audits`);
    }

    rtwInvite(applicationId: number) {
        return this._resolveUrl(`${this._baseUrlWithVersion}/Application/${applicationId}/rtw/reinvite`);
    }

    rtwMarkAsSeen(applicationId: number, rtwId: number) {
        return this._resolveUrl(`${this._baseUrlWithVersion}/Application/${applicationId}/rtw/${rtwId}/seen`);
    }

    rtwOutcome(applicationId: number, rtwId: number, outcomeId: number) {
        return this._resolveUrl(`${this._baseUrlWithVersion}/Application/${applicationId}/rtw/${rtwId}/outcome/${outcomeId}`);
    }

    rtwReviewDate(applicationId: number, rtwId: number) {
        return this._resolveUrl(`${this._baseUrlWithVersion}/Application/${applicationId}/rtw/${rtwId}/review`);
    }

    // not implemented
    lockApplication(applicationId: number) {
        return this._resolveUrl(`${this._baseUrlWithVersion}/Application/${applicationId}/lock`);
    }

    superlockApplication(applicationId: number, lock: boolean) {
        if(lock){
            return this._resolveUrl(`${this._baseUrlWithVersion}/Application/${applicationId}/SuperLock`);
        } else {
            return this._resolveUrl(`${this._baseUrlWithVersion}/Application/${applicationId}/SuperUnlock`);
        }
    }

    systemRights() {
        return this._resolveUrl(`${this._baseUrlSimple}/system-rights`);
    }

    organisationSearch() : string {
        return this._resolveUrl(`${this._baseUrlSimple}/organisations/search`);
    }

    createOrganisation() : string {
        return this._resolveUrl(`${this._baseUrlSimple}/organisations`);
    }

    organisationById(organisationId: number) : string {
        return this._resolveUrl(`${this._baseUrlSimple}/organisations/${organisationId}`);
    }

    organisationUsers(organisationId: number) : string {
        return this._resolveUrl(`${this._baseUrlSimple}/organisations/${organisationId}/users`);
    }

    organisationUserById(organisationId: number, organisationUserId: number) : string {
        return this._resolveUrl(`${this._baseUrlSimple}/organisations/${organisationId}/users/${organisationUserId}`);
    }

    organisationLocations(organisationId: number) : string {
        return this._resolveUrl(`${this._baseUrlSimple}/organisations/${organisationId}/locations`);
    }

    organisationLocationById(organisationId: number, locationId: number) : string {
        return this._resolveUrl(`${this._baseUrlSimple}/organisations/${organisationId}/locations/${locationId}`);
    }

    organisationAudit(organisationId: number) : string {
        return this._resolveUrl(`${this._baseUrlSimple}/organisations/${organisationId}/audit`);
    }

    organisationAuthorisation(organisationId: number) : string {
        return this._resolveUrl(`${this._baseUrlSimple}/organisations/${organisationId}/authorisation`);
    }

    organisationChangeImage(organisationId: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/organisations/${organisationId}/ChangeImage`);
    }

    organisationImage(imageId: string) {
        if(imageId === '' || imageId === null || imageId === undefined) {
            return '';
        }
        return this._resolveUrl(`https://nexgenavatars.blob.core.windows.net/{clientId}/organisations/${imageId.toLowerCase()}.jpg`);
    }

    organisationLists() {
        return this._resolveUrl(`${this._baseUrlSimple}/organisations/lists`);
    }

    // Application Custom Forms
    applicationLists() {
        return this._resolveUrl(`${this._baseUrlSimple}/application-lists`);
    }

    applicationListValues(applicationId: number) {
        return this._resolveUrl(`${this._baseUrlSimple}/application/${applicationId}/lists`);
    }
}
